import { useMemo, type JSX } from 'react';
import type { QuestionAnswerDistributionType } from 'types/api/diagnosisReportRefResponseType';

import '../../assets/styles/ws-report.scss';
import { formatNumberToCurrency } from 'utils/app';
import {
  computeMaxOptionsCount,
  computeOptionTotalCount,
  getColorClass,
  getOptionPercentageLabel,
  getOptionTitle,
  getQuestionLabel,
  sortOptionsByNo,
} from './wsReportQuestionAnswer.utils';

/**
 * 質問別回答選択パターン（各質問でどの選択肢が何人選ばれたか）を表示するコンポーネント。
 * 質問文・選択肢数はコンテンツごとにAPIから取得する
 */
export default function WsReportQuestionAnswer(props: {
  questionAnswerDistribution: QuestionAnswerDistributionType[];
}): JSX.Element {
  const questions = props.questionAnswerDistribution;

  const maxOptionsCount = useMemo(() => computeMaxOptionsCount(questions), [questions]);

  return (
    <section className="section-chart section-question-answer section-question-answer-full">
      <div className="card card-chart">
        <div className="card-header">
          <h3 className="card-title">質問別回答選択パターン</h3>
          <p className="card-subtitle">
            各質問の選択肢ごとの回答数（設問数・選択肢数はコンテンツにより異なります）
          </p>
        </div>
        <div className="card-body pt-3">
          {questions.length > 0 && maxOptionsCount > 0 ? (
            <>
              <div className="question-answer-grid question-answer-grid-compact">
                <table className="question-answer-table question-answer-table-compact">
                  <thead>
                    <tr>
                      <th className="question-col">設問</th>
                      {Array.from({ length: maxOptionsCount }, (_, i) => (
                        <th key={i} className="option-col">
                          選択肢{i + 1}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map(question => {
                      const totalCount = computeOptionTotalCount(question.options);
                      const sortedOptions = sortOptionsByNo(question.options);
                      const questionLabel = getQuestionLabel(question);
                      return (
                        <tr key={`q-${question.questionNo}`}>
                          <td className="question-cell">{questionLabel}</td>
                          {Array.from({ length: maxOptionsCount }, (_, i) => {
                            const option = sortedOptions[i];
                            if (!option) {
                              return (
                                <td key={i} className="option-cell empty">
                                  —
                                </td>
                              );
                            }
                            const percentage = getOptionPercentageLabel(option.count, totalCount);
                            const colorClass = getColorClass(option.count, totalCount);
                            const optionTitle = getOptionTitle(option);
                            return (
                              <td key={i} className={`option-cell ${colorClass}`}>
                                <span className="option-cell-title">
                                  {optionTitle}
                                  {option.isCorrect && (
                                    <span className="option-correct-badge ms-1">正解</span>
                                  )}
                                </span>
                                <span className="option-cell-meta">
                                  {formatNumberToCurrency(option.count)}名 （{percentage}
                                  %）
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="selection-rate-legend mt-3">
                <span className="legend-title">選択率:</span>
                <span className="legend-item high">
                  <span className="legend-color high" />高 (35%以上)
                </span>
                <span className="legend-item medium">
                  <span className="legend-color medium" />中 (25-35%)
                </span>
                <span className="legend-item low">
                  <span className="legend-color low" />低 (15-25%)
                </span>
                <span className="legend-item lowest">
                  <span className="legend-color lowest" />
                  最低 (15%未満)
                </span>
              </div>
            </>
          ) : (
            <div className="text-center p-4">
              <p>データがありません</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
